import { useTranslation } from 'react-i18next'
import { Select } from '@ludus/ui'
import type { PipelineId } from '@ludus/core'
import type { FlowNode } from '@/features/flows/types.ts'
import { useStatusChoices } from './useDrawerOptions.ts'

/**
 * The trigger drawer, written as a sentence rather than a field list (D5):
 * *When a lead in {this pipeline} enters [status] (from [any | status])*.
 *
 * It is the one drawer that does not come out of `FieldControl`, and the reason
 * is that the two options are not independent: `fromStatusId` only means
 * anything as a qualifier of `statusId`, and two stacked pickers labelled
 * "statusId" and "fromStatusId" would leave a person to infer the relationship
 * the sentence simply states.
 *
 * Both pickers offer the **flow's own** board — a trigger watches the pipeline
 * the flow belongs to, which is why neither field carries a `scopedBy`.
 *
 * Unlike the target-status picker in `create_lead`, an `awaiting_distribution`
 * status is perfectly legal here: D6 forbids a flow *landing a card on* the
 * distribution trigger, not a flow *reacting to* one.
 */

interface TriggerSentenceProps {
  node: FlowNode
  options: Record<string, unknown>
  pipelineId: PipelineId
  onChange: (name: string, value: unknown) => void
  issueFor: (name: string) => string | undefined
}

/** `fromStatusId: null` means "from any status" — the field's own default. */
const ANY = ''

export function TriggerSentence({ options, pipelineId, onChange, issueFor }: TriggerSentenceProps) {
  const { t } = useTranslation()
  const statuses = useStatusChoices(pipelineId)

  const statusOptions = statuses.choices.map((s) => ({ value: s.id, label: s.name }))
  const statusId = typeof options.statusId === 'string' ? options.statusId : ''
  const fromStatusId = typeof options.fromStatusId === 'string' ? options.fromStatusId : ANY

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] leading-snug text-muted">
        {t('pages.flows.drawer.trigger.lead', {
          defaultValue: 'When a lead in this pipeline enters',
        })}
      </p>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wide text-muted">
          {t('pages.flows.canvas.option.statusId', { defaultValue: 'statusId' })}
          <span aria-hidden="true"> *</span>
        </span>
        <Select
          value={statusId}
          onValueChange={(next) => onChange('statusId', next)}
          placeholder={t('pages.flows.drawer.pick.status', { defaultValue: 'Pick a status' })}
          options={statusOptions}
        />
        {issueFor('statusId') !== undefined && (
          <span className="text-[11px] text-toast-error-text">{issueFor('statusId')}</span>
        )}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wide text-muted">
          {t('pages.flows.drawer.trigger.from', { defaultValue: 'coming from' })}
        </span>
        <Select
          value={fromStatusId}
          onValueChange={(next) => onChange('fromStatusId', next === ANY ? null : next)}
          options={[
            {
              value: ANY,
              label: t('pages.flows.drawer.trigger.any', { defaultValue: 'any status' }),
            },
            ...statusOptions,
          ]}
        />
      </label>

      <p className="text-[11px] leading-snug text-muted">
        {t('pages.flows.drawer.trigger.note', {
          defaultValue:
            'A card first seen already in this status counts as entering it, so the automation fires for both.',
        })}
      </p>
    </div>
  )
}
